import { useInView } from '../hooks/useInView';
import { Quote } from 'lucide-react';

const logos = ['Company 1', 'Company 2', 'Company 3', 'Company 4', 'Company 5', 'Company 6'];

interface Testimonial {
  quote: string;
  name: string;
  title: string;
  company: string;
}

const testimonials: Testimonial[] = [
  {
    quote:
      'Opair gave us complete visibility into how AI is being used across 12 engineering teams. We went from zero governance to full compliance in under a week.',
    name: 'Sarah Chen',
    title: 'VP of Engineering',
    company: 'TechCorp Industries',
  },
  {
    quote:
      "The policy engine is incredibly powerful. We can enforce different rules for different teams and use cases without any friction for developers. It's like having a CISO embedded in every API call.",
    name: 'Marcus Rodriguez',
    title: 'Chief Information Security Officer',
    company: 'FinanceScale',
  },
];

export function SocialProof() {
  const [headingRef, headingInView] = useInView();

  return (
    <section
      id="social-proof"
      className="relative py-24 sm:py-32 bg-gray-950"
      aria-labelledby="social-proof-heading"
    >
      <div
        className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-indigo-500/30 to-transparent"
        aria-hidden="true"
      />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div
          ref={headingRef}
          className={`text-center mb-16 transition-all duration-700 ${
            headingInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 id="social-proof-heading" className="text-3xl sm:text-4xl font-bold text-white">
            Trusted by leading enterprises
          </h2>
          <p className="mt-4 text-lg text-gray-400">
            Organizations across industries rely on Opair to govern their AI usage.
          </p>
        </div>

        {/* Logo grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-16">
          {logos.map((logo, i) => (
            <LogoPlaceholder key={logo} name={logo} index={i} />
          ))}
        </div>

        {/* Testimonials */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {testimonials.map((t, i) => (
            <TestimonialCard key={t.name} testimonial={t} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function LogoPlaceholder({ name, index }: { name: string; index: number }) {
  const [ref, isInView] = useInView();

  return (
    <div
      ref={ref}
      className={`flex items-center justify-center rounded-xl border border-gray-800 bg-gray-900/30 px-6 py-8 transition-all duration-500 ${
        isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
      style={{ transitionDelay: `${index * 60}ms` }}
    >
      <span className="text-sm font-medium text-gray-500">{name}</span>
    </div>
  );
}

function TestimonialCard({ testimonial, index }: { testimonial: Testimonial; index: number }) {
  const [ref, isInView] = useInView();

  return (
    <div
      ref={ref}
      className={`relative rounded-2xl border border-gray-800 bg-gray-900/50 p-8 transition-all duration-700 ${
        isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 150}ms` }}
    >
      <Quote className="h-8 w-8 text-indigo-500/30 mb-4" aria-hidden="true" />
      <blockquote className="text-base text-gray-300 leading-relaxed">
        &ldquo;{testimonial.quote}&rdquo;
      </blockquote>
      <div className="mt-6 flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-indigo-500/20 flex items-center justify-center">
          <span className="text-sm font-bold text-indigo-400">{testimonial.name.charAt(0)}</span>
        </div>
        <div>
          <div className="text-sm font-semibold text-white">{testimonial.name}</div>
          <div className="text-xs text-gray-500">
            {testimonial.title}, {testimonial.company}
          </div>
        </div>
      </div>
    </div>
  );
}
